/**
 * MKS-203 — avatar upload/reset mutation hooks.
 *
 * Both mutations repaint every avatar surface on success: `refreshUser()`
 * re-populates `authContext.user.avatarUrl` (top bar, MyTasksTopBar), and
 * invalidating the `['board']` query prefix repaints every kanban board the
 * user appears on as an assignee/watcher/team member, regardless of which
 * project or audience it's keyed under.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../services/authContext';
import { useErrorToast } from './queryUtils';
import { uploadAvatar, resetAvatar } from '../services/api/avatar';

function useRepaintAfterAvatarChange() {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  return async () => {
    await refreshUser();
    await queryClient.invalidateQueries({ queryKey: ['board'] });
  };
}

export function useUploadAvatar() {
  const onError = useErrorToast();
  const repaint = useRepaintAfterAvatarChange();
  return useMutation({
    mutationFn: (blob: Blob) => uploadAvatar(blob),
    onSuccess: () => {
      void repaint();
    },
    onError: (err) => onError(err, "Couldn't upload your photo"),
  });
}

export function useResetAvatar() {
  const onError = useErrorToast();
  const repaint = useRepaintAfterAvatarChange();
  return useMutation({
    mutationFn: () => resetAvatar(),
    onSuccess: () => {
      void repaint();
    },
    onError: (err) => onError(err, "Couldn't reset your photo"),
  });
}
